/* eslint-disable max-lines -- gate + content share this file; passing the resolved opt-in flag into content is a few prettier-wrapped lines over 300. */
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@heidi/design-system/page-shell';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import { type ModalId, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useQueryUser } from '@heidi/workspace/data';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  type MouseEvent,
  type Ref,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import type { SideNavCreditUsagePopoverContext } from '../../../contracts/side-nav-credit-usage-popover-analytics.types';
import { SIDE_NAV_CREDIT_USAGE_POPOVER_MODAL_ID } from '../../../contracts/side-nav-credit-usage-popover-constants';
import type { CreditAllowanceResponse } from '../../../contracts/types/credit-allowance.types';
import { useDevOverriddenCreditAllowance } from '../../../data/billing-dev/upgrade-nudges-overrides';
import { useReverseTrial, useUserTier } from '../../../data/injected-billing';
import {
  getCreditAllowanceNudgeVariant,
  getCreditAllowanceUsagePercent,
  isComplimentaryCreditAllowance,
} from '../../../data/query/credit-allowance.utils';
import { useQueryUserBillingInfo } from '../../../data/query/useQueryUserBillingInfo';
import {
  getCreditAllowanceThresholdBillingPlanKey,
  isCreditAllowanceThresholdBillingPlanResolved,
} from '../../settings/get-credit-allowance-threshold-billing-plan-key';
import { isHeidi2OptInCreditUsageEnabled } from '../../settings/is-credit-usage-surface-enabled';
import {
  resolveCreditUsagePopoverPlan,
  resolveCreditUsagePopoverTargetPlan,
} from '../../settings/resolve-credit-usage-popover-plan';
import { showsCreditUsageMeterTrialCountdown } from '../../settings/shows-credit-usage-meter-trial-countdown';
import { useComplimentaryTrialEndState } from '../../settings/use-complimentary-trial-end-state';
import { useIsClinicianMaxBillingSeat } from '../../settings/use-is-clinician-max-billing-seat';
import { useIsCreditUsageSurfaceEnabled } from '../../settings/use-is-credit-usage-surface-enabled';
import { useIsMaxBillingSeat } from '../../settings/use-is-max-billing-seat';
import { useTeamMaxUpgradeState } from '../../settings/use-team-max-upgrade-state';
import { CLINICIAN_MAX_UPGRADE_ROW_INK_CLASS } from '../clinician-max-upgrade-theme';
import { ClinicianTrialNavCard } from '../clinician-trial-end/clinician-trial-nav-card';
import { useClinicianTrialEndState } from '../clinician-trial-end/use-clinician-trial-end-state';
import { useClinicianTrialExtensionAvailable } from '../clinician-trial-end/use-clinician-trial-extension-available';
import { complimentaryTrialEndModalEntryPointAtom } from '../complimentary-trial-end/complimentary-trial-end-atoms';
import { CreditUsageIndicatorPopover } from './credit-usage-indicator-popover';
import { CreditUsageTriggerIcon } from './credit-usage-trigger-icon';
import {
  creditAllowanceThresholdConsumedAtom,
  isThresholdConsumedInRecord,
  markCreditAllowanceThresholdConsumedAtom,
} from './creditAllowanceThresholdStorage';
import { sideNavCreditUsagePopoverMessages } from './side-nav-credit-usage-popover-messages';
import { UsageNudgeCard } from './usage-nudge-card';
import { useHoverOpen } from './use-hover-open';
import { useSideNavUsageNudge } from './use-side-nav-usage-nudge';

/**
 * The modal openers this surface reaches today through `usePaywallStates`
 * and the app atom setters. The Clinician Max opener carries the entry
 * point write that precedes it at the call site, so the pair cannot separate.
 */
export type SideNavCreditUsagePopoverUpgradeActions = {
  openClinicianMaxUpgradeModal: () => void;
  openClinicianUpgradeModal: () => void;
  openPracticeUpgradeModal: () => void;
  openPricingPlansModal: () => void;
  openTrialExtensionModal: () => void;
  /** A Practice member asks their admin for a Max seat. */
  openTeamSeatUpgradeRequestModal: () => void;
};

/**
 * Every app input this surface still needs, supplied BY REFERENCE: the slot
 * renders the popover only for the cohort that sees it, and passing a hook's
 * value would run the allowance query and its PostHog exposures for everyone
 * else. Each member is called exactly where its app expression is called today.
 */
export type SideNavCreditUsagePopoverPorts = {
  useIsHeidi2OptInEnabled: () => boolean;
  useIsHeidi2FullUpgradeFlowEnabled: () => boolean;
  /** `release-growth-heidi-2-trial-end`: last-week countdown of complimentary usage. */
  useIsHeidi2TrialEndEnabled: () => boolean;
  /** `release-growth-heidi-2-clinician-trial-end`: last-week countdown of a new user's Clinician trial. */
  useIsHeidi2ClinicianTrialEndEnabled: () => boolean;
  /** `release-heidi-2-team-upgrades`: a Practice admin is sold Max seats for the team. */
  useIsHeidi2TeamUpgradesEnabled: () => boolean;
  /** `release-growth-heidi-2-usage-nudges`: per-tier usage card at 90% and 100%. */
  useIsHeidi2UsageNudgesEnabled: () => boolean;
  /** `useIsTeamSeatUpgradeRequester`: a non-admin member who may ask for a seat upgrade. */
  useCanRequestTeamSeatUpgrade: () => boolean;
  /** `next/router` push, for the usage card's seat checkout links. */
  useNavigate: () => (href: string) => void;
  useIsRibbonSidebarEnabled: () => boolean;
  useMobilePanel: () => { dismissMobileSidebar: () => void };
  useIsGamifyTrialExtensionEntryEnabled: () => boolean;
  /** The trial extension checklist is open to this member and not yet completed. */
  useIsGamifyTrialExtensionAvailable: () => boolean;
  useOfflineGatedTooltip: () => { isOfflineGated: boolean };
  useIsPracticeMaxUpgradeAvailable: () => { isPracticeMaxUpgradeAvailable: boolean };
  useQueryCreditAllowance: () => { data: CreditAllowanceResponse | undefined };
  useQueuedModal: (modalId: ModalId) => { isOpen: boolean; handleClose: () => void };
  useUpgradeActions: () => SideNavCreditUsagePopoverUpgradeActions;
  isAppleCompliantBuild: () => boolean;
  getUsageLimitsHelpUrl: (locale?: string) => string;
  trackSideNavCreditUsagePopoverViewed: (
    context: SideNavCreditUsagePopoverContext
  ) => void;
  trackSideNavCreditUsagePopoverUpgradeClick: (
    context: SideNavCreditUsagePopoverContext & { label: string }
  ) => void;
  trackSideNavCreditUsagePopoverExtendTrialClick: (
    context: SideNavCreditUsagePopoverContext & { label: string }
  ) => void;
  trackSideNavCreditUsagePopoverCloseClick: (
    context: SideNavCreditUsagePopoverContext & { label: string }
  ) => void;
};

type SideNavCreditUsagePopoverProps = {
  ports: SideNavCreditUsagePopoverPorts;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  'data-testid'?: string;
};

export function SideNavCreditUsagePopover(props: SideNavCreditUsagePopoverProps) {
  const { ports } = props;
  const isHeidi2OptInEnabled = ports.useIsHeidi2OptInEnabled();
  const isCreditUsageEnabled = useIsCreditUsageSurfaceEnabled({
    isHeidi2OptInEnabled,
  });

  if (!isCreditUsageEnabled) return null;

  return (
    <SideNavCreditUsagePopoverContent
      {...props}
      isHeidi2OptInEnabled={isHeidi2OptInEnabled}
    />
  );
}

function SideNavCreditUsagePopoverContent({
  ports,
  side = 'right',
  align = 'end',
  'data-testid': testId = 'side-nav-credit-usage-popover',
  isHeidi2OptInEnabled,
}: SideNavCreditUsagePopoverProps & {
  isHeidi2OptInEnabled: boolean;
}) {
  const intl = useIntl();
  const { collapsible, isMobile } = useSidebar();
  const { dismissMobileSidebar } = ports.useMobilePanel();
  const isRibbonSidebarEnabled = ports.useIsRibbonSidebarEnabled();
  const isHeidi2FullUpgradeFlowEnabled = ports.useIsHeidi2FullUpgradeFlowEnabled();
  const { data: user } = useQueryUser();
  const { data: billingInfo } = useQueryUserBillingInfo();
  const isOptInCreditUsageEnabled = isHeidi2OptInCreditUsageEnabled({
    isHeidi2OptInEnabled,
    heidiVersion: billingInfo?.heidi_version,
  });
  const { data: queriedAllowance } = ports.useQueryCreditAllowance();
  const allowance = useDevOverriddenCreditAllowance(queriedAllowance);
  const { newUserTier } = useUserTier();
  const {
    isClinicianPracticeTrialUser,
    isClinicianTrialUser,
    clinicianTrialDaysLeft,
    clinicianTrialHoursLeft,
  } = useReverseTrial();
  const {
    openClinicianMaxUpgradeModal,
    openClinicianUpgradeModal,
    openPracticeUpgradeModal,
    openPricingPlansModal,
    openTrialExtensionModal,
    openTeamSeatUpgradeRequestModal,
  } = ports.useUpgradeActions();
  const isUsageNudgesEnabled = ports.useIsHeidi2UsageNudgesEnabled();
  const canRequestTeamSeatUpgrade = ports.useCanRequestTeamSeatUpgrade();
  const navigate = ports.useNavigate();
  const isTrialExtensionEntryEnabled = ports.useIsGamifyTrialExtensionEntryEnabled();
  const { isOfflineGated } = ports.useOfflineGatedTooltip();
  const upgradeCtaLabel = isHeidi2FullUpgradeFlowEnabled
    ? intl.formatMessage(sideNavCreditUsagePopoverMessages.upgrade)
    : undefined;
  const consumedThresholds = useAtomValue(creditAllowanceThresholdConsumedAtom);
  const markThresholdConsumed = useSetAtom(markCreditAllowanceThresholdConsumedAtom);
  const [isManualOpen, setIsManualOpen] = useState(false);
  const { isOpen: isQueuedOpen, handleClose: handleQueuedClose } = ports.useQueuedModal(
    SIDE_NAV_CREDIT_USAGE_POPOVER_MODAL_ID
  );
  const plan = resolveCreditUsagePopoverPlan(newUserTier, isClinicianPracticeTrialUser);
  const isOnClinicianMax = useIsClinicianMaxBillingSeat();
  // Any Max seat: overage copy, no consumed-usage line. Practice Max has nothing
  // further to sell; Clinician Max sells Practice Max once that is available.
  const isOnMaxSeat = useIsMaxBillingSeat();
  const isOnPracticeMax = isOnMaxSeat && !isOnClinicianMax;
  // A Practice admin still has Max seats to sell while any member is left on
  // Standard, whatever their own seat; those go through the pricing plans modal.
  const isTeamUpgradesEnabled = ports.useIsHeidi2TeamUpgradesEnabled();
  const { hasMembersLeftToUpgrade } = useTeamMaxUpgradeState({ isTeamUpgradesEnabled });
  const sellsTeamMaxSeats = hasMembersLeftToUpgrade === true;
  const { isPracticeMaxUpgradeAvailable } = ports.useIsPracticeMaxUpgradeAvailable();
  // Only the pricing plans modal (full upgrade flow) can sell Practice Max; the
  // pre-flag single modal checks out Clinician Max, which they already have.
  const sellsPracticeMaxToClinicianMax =
    isOnClinicianMax && isPracticeMaxUpgradeAvailable && isHeidi2FullUpgradeFlowEnabled;
  const targetPlan = sellsPracticeMaxToClinicianMax
    ? SubscriptionTier.CLINICIAN_PRO_PLUS
    : resolveCreditUsagePopoverTargetPlan(plan);
  const isTrialing = showsCreditUsageMeterTrialCountdown({
    isClinicianTrialUser,
    isClinicianPracticeTrialUser,
  });
  const trialEndState = useComplimentaryTrialEndState({
    isHeidi2OptInEnabled,
    isTrialEndEnabled: ports.useIsHeidi2TrialEndEnabled(),
    allowance,
  });
  // Only active members get the sidebar countdown; inactive members keep the
  // usual tile and are nudged by the banner instead.
  const trialEnd = trialEndState?.cohort === 'active' ? trialEndState : null;
  const setTrialEndModalEntryPoint = useSetAtom(complimentaryTrialEndModalEntryPointAtom);
  const clinicianTrialEndState = useClinicianTrialEndState({
    isTrialEndEnabled: ports.useIsHeidi2ClinicianTrialEndEnabled(),
    isAppleCompliantBuild: ports.isAppleCompliantBuild(),
  });
  const clinicianTrialLastWeek =
    clinicianTrialEndState?.phase === 'last_7' ? clinicianTrialEndState : null;
  // A running Clinician trial: the tile opens the trial card instead of upgrading.
  const clinicianTrialCard =
    clinicianTrialEndState != null && clinicianTrialEndState.phase !== 'ended'
      ? { ...clinicianTrialEndState, phase: clinicianTrialEndState.phase }
      : null;
  const isTrialExtensionAvailable = useClinicianTrialExtensionAvailable(
    ports.useIsGamifyTrialExtensionAvailable()
  );
  // Runs once the card has finished closing, so the next dialog opens cleanly.
  const afterCardCloseRef = useRef<(() => void) | null>(null);
  // Standard checkout is live for free/trial Heidi 2 users; Max still waits on its flag.
  const upgradeSeat =
    isOptInCreditUsageEnabled && (isTrialing || newUserTier === SubscriptionTier.FREE)
      ? 'standard'
      : 'max';
  const isMaxUpgradeEnabled = isHeidi2FullUpgradeFlowEnabled;
  const usagePercent = allowance == null ? 0 : getCreditAllowanceUsagePercent(allowance);
  // Side-nav click skips the usage popover and opens the upgrade modal
  // (complimentary uncapped cycles and capped cycles after that period).
  const isComplimentaryAllowance = isComplimentaryCreditAllowance(allowance);
  // The last week of complimentary usage or of a Clinician trial counts down on
  // the tile from its own end date.
  const lastWeekCountdown = trialEnd ?? clinicianTrialLastWeek;
  const trialRemaining = !isTrialing
    ? undefined
    : clinicianTrialHoursLeft < 24
      ? { unit: 'hours' as const, value: clinicianTrialHoursLeft }
      : { unit: 'days' as const, value: clinicianTrialDaysLeft };
  const nudge = useSideNavUsageNudge({
    isEnabled: isUsageNudgesEnabled && isOptInCreditUsageEnabled,
    allowance,
    isTrialing,
    isTeamUpgradesEnabled,
    canRequestTeamSeatUpgrade,
    isAppleCompliantBuild: ports.isAppleCompliantBuild(),
  });
  // Blue filled tile for a running countdown (trial or the last week of
  // complimentary usage), and once a usage card is due. Everyone else gets the
  // outline secondary icon.
  const triggerVariant =
    isTrialing || lastWeekCountdown != null || nudge.trigger.forceTile ? 'tile' : 'plain';
  const nudgeCard = nudge.card;
  // The usage card replaces the hover label and the click-through.
  const showsCardOnHover = nudgeCard != null;
  const clickOpensCard = nudgeCard != null;
  // At 90% and above the usage card takes the tile from the trial card.
  const trialCard = nudgeCard == null ? clinicianTrialCard : null;
  const tooltipLabel = intl.formatMessage(
    isComplimentaryAllowance
      ? sideNavCreditUsagePopoverMessages.upgrade
      : sideNavCreditUsagePopoverMessages.tooltip
  );
  // Trial users see the countdown on hover for the whole trial, not just the
  // 7-day window the tile number covers.
  const trialCountdownLabel =
    trialEnd != null
      ? intl.formatMessage(
          sideNavCreditUsagePopoverMessages.complimentaryDaysLeftTooltip,
          {
            count: trialEnd.daysLeft,
          }
        )
      : clinicianTrialLastWeek != null
        ? intl.formatMessage(
            sideNavCreditUsagePopoverMessages.clinicianTrialDaysLeftTooltip,
            { count: clinicianTrialLastWeek.daysLeft }
          )
        : trialRemaining == null
          ? undefined
          : intl.formatMessage(
              trialRemaining.unit === 'hours'
                ? sideNavCreditUsagePopoverMessages.trialHoursLeftTooltip
                : sideNavCreditUsagePopoverMessages.trialDaysLeftTooltip,
              { count: trialRemaining.value }
            );
  const sideOffset = isRibbonSidebarEnabled && !isMobile ? 12 : 8;
  const useSidebarTrigger = collapsible === 'label-icons' && !isMobile;
  const analyticsContextRef = useRef({ usagePercent: 0 });
  const hasTrackedViewRef = useRef(false);
  // Resolved below, after handleUpgradeClick. Read only from the click handler.
  const directUpgradeRef = useRef<(() => void) | undefined>(undefined);
  const { trackSideNavCreditUsagePopoverViewed } = ports;

  const trackViewed = useCallback(
    (usagePercent: number) => {
      analyticsContextRef.current.usagePercent = usagePercent;
      hasTrackedViewRef.current = true;
      trackSideNavCreditUsagePopoverViewed({
        usagePercent,
        targetPlan,
        ...(newUserTier != null && { currentPlan: newUserTier }),
      });
    },
    [newUserTier, targetPlan, trackSideNavCreditUsagePopoverViewed]
  );

  const markThresholdConsumedIfApplicable = useCallback(() => {
    if (
      allowance == null ||
      !isCreditAllowanceThresholdBillingPlanResolved(billingInfo)
    ) {
      return;
    }

    const threshold = getCreditAllowanceNudgeVariant(allowance);
    if (threshold == null) {
      return;
    }

    const billingPlanKey = getCreditAllowanceThresholdBillingPlanKey(billingInfo);
    if (
      isThresholdConsumedInRecord(
        consumedThresholds,
        billingPlanKey,
        allowance.cycle_end,
        threshold
      )
    ) {
      return;
    }

    markThresholdConsumed({
      billingPlanKey,
      cycleEnd: allowance.cycle_end,
      threshold,
    });
  }, [allowance, billingInfo, consumedThresholds, markThresholdConsumed]);

  const hover = useHoverOpen({
    isEnabled: showsCardOnHover && !isMobile,
    onHoverClose: markThresholdConsumedIfApplicable,
  });
  const { isHoverOpen, close: closeHover } = hover;
  const isOpen = isQueuedOpen || isManualOpen || isHoverOpen;

  useEffect(() => {
    // Mobile renders a billing row, not this popover. Don't record a view
    // for a launch the user never saw.
    if (!isOpen || isMobile) {
      hasTrackedViewRef.current = false;
      return;
    }
    if (hasTrackedViewRef.current || allowance == null) {
      return;
    }

    trackViewed(getCreditAllowanceUsagePercent(allowance));
  }, [allowance, isMobile, isOpen, trackViewed]);

  const handleQueuedModalClose = useCallback(() => {
    markThresholdConsumedIfApplicable();
    handleQueuedClose();
  }, [handleQueuedClose, markThresholdConsumedIfApplicable]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean, context?: { usagePercent: number }) => {
      if (nextOpen) {
        // Side-nav click goes straight to the upgrade modal. The queued launch
        // path still shows the popover when the queue surfaces it.
        if (!isQueuedOpen && directUpgradeRef.current != null) {
          if (context != null) {
            analyticsContextRef.current.usagePercent = context.usagePercent;
          }
          markThresholdConsumedIfApplicable();
          directUpgradeRef.current();
          return;
        }
        if (context != null && !hasTrackedViewRef.current) {
          trackViewed(context.usagePercent);
        }
        // Launch path already owns the queue slot, and a hover open stays a
        // hover open until the pointer leaves — user click opens locally.
        if (!isQueuedOpen && !isHoverOpen) {
          setIsManualOpen(true);
        }
        return;
      }
      hasTrackedViewRef.current = false;
      if (isHoverOpen) {
        closeHover();
      }
      if (isManualOpen) {
        markThresholdConsumedIfApplicable();
        setIsManualOpen(false);
      }
      if (isQueuedOpen) {
        handleQueuedModalClose();
      }
    },
    [
      handleQueuedModalClose,
      closeHover,
      isHoverOpen,
      isQueuedOpen,
      isManualOpen,
      markThresholdConsumedIfApplicable,
      trackViewed,
    ]
  );

  // A click while the card is hover-open does what a click does without it:
  // pin a usage card, or run the button's action.
  const handleTriggerClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (!isHoverOpen) return;
      event.preventDefault();
      closeHover({ notify: false });
      if (directUpgradeRef.current != null) {
        markThresholdConsumedIfApplicable();
        directUpgradeRef.current();
        return;
      }
      setIsManualOpen(true);
    },
    [closeHover, isHoverOpen, markThresholdConsumedIfApplicable]
  );

  const { trackSideNavCreditUsagePopoverUpgradeClick } = ports;
  const handleUpgradeClick = useCallback(() => {
    const { usagePercent } = analyticsContextRef.current;
    const isStandardUpgrade = upgradeSeat === 'standard';
    trackSideNavCreditUsagePopoverUpgradeClick({
      usagePercent,
      targetPlan,
      label:
        upgradeCtaLabel ??
        (isStandardUpgrade
          ? intl.formatMessage(sideNavCreditUsagePopoverMessages.upgrade)
          : intl.formatMessage(
              { defaultMessage: 'Upgrade to {planName}', id: 'zynTUYplVi' },
              {
                planName: intl.formatMessage(
                  plan === 'practiceMax'
                    ? { defaultMessage: 'Practice Max', id: 'TsAzRWYZ5I' }
                    : { defaultMessage: 'Clinician Max', id: 'Cn07oTAjik' }
                ),
              }
            )),
      ...(newUserTier != null && { currentPlan: newUserTier }),
    });
    // Full-upgrade-flow alone — billing-page Upgrade also requires Heidi 2 opt-in.
    if (isHeidi2FullUpgradeFlowEnabled || sellsTeamMaxSeats) {
      openPricingPlansModal();
      return;
    }
    if (isStandardUpgrade) {
      if (plan === 'practiceMax') {
        openPracticeUpgradeModal();
      } else {
        openClinicianUpgradeModal();
      }
      return;
    }
    openClinicianMaxUpgradeModal();
  }, [
    intl,
    isHeidi2FullUpgradeFlowEnabled,
    newUserTier,
    openClinicianMaxUpgradeModal,
    openClinicianUpgradeModal,
    openPracticeUpgradeModal,
    openPricingPlansModal,
    plan,
    sellsTeamMaxSeats,
    targetPlan,
    trackSideNavCreditUsagePopoverUpgradeClick,
    upgradeCtaLabel,
    upgradeSeat,
  ]);

  // Clinician Max still upgrades from an uncapped cycle, and from a capped one
  // once Practice Max is on sale. Practice Max has nothing further to sell for
  // the member themselves, so the popover stays with no action unless they are
  // an admin with members still to move to Max.
  const upgradeAction =
    ports.isAppleCompliantBuild() ||
    (isOnPracticeMax && !sellsTeamMaxSeats) ||
    (isOnClinicianMax && !isComplimentaryAllowance && !sellsPracticeMaxToClinicianMax) ||
    (upgradeSeat === 'max' && !isMaxUpgradeEnabled && !sellsTeamMaxSeats)
      ? undefined
      : handleUpgradeClick;
  // In the last week of complimentary usage the tile opens that T-7 modal,
  // which carries its own upgrade paths, instead of going straight to checkout.
  const trialEndAction = useMemo(() => {
    if (ports.isAppleCompliantBuild()) return undefined;
    if (trialEnd != null) return () => setTrialEndModalEntryPoint('side_nav');
    return undefined;
  }, [ports, setTrialEndModalEntryPoint, trialEnd]);
  const nudgeCta = nudge.cta;
  const handleNudgeCtaClick = useCallback(() => {
    if (nudgeCta == null) return;
    trackSideNavCreditUsagePopoverUpgradeClick({
      usagePercent: analyticsContextRef.current.usagePercent,
      targetPlan,
      label: nudgeCta.label,
      ...(newUserTier != null && { currentPlan: newUserTier }),
    });
    markThresholdConsumedIfApplicable();
    if (nudgeCta.kind === 'checkout') {
      navigate(nudgeCta.href);
    } else if (nudgeCta.kind === 'requestUpgrade') {
      openTeamSeatUpgradeRequestModal();
    } else {
      openPricingPlansModal();
    }
  }, [
    markThresholdConsumedIfApplicable,
    navigate,
    newUserTier,
    nudgeCta,
    openPricingPlansModal,
    openTeamSeatUpgradeRequestModal,
    targetPlan,
    trackSideNavCreditUsagePopoverUpgradeClick,
  ]);
  const primaryAction =
    trialEndAction ??
    upgradeAction ??
    (nudgeCta != null ? handleNudgeCtaClick : undefined);
  // A usage or trial card is the click target; mobile keeps the upgrade row.
  directUpgradeRef.current =
    clickOpensCard || trialCard != null ? undefined : primaryAction;
  const handleMobileClick = useCallback(() => {
    analyticsContextRef.current.usagePercent = usagePercent;
    dismissMobileSidebar();
    primaryAction?.();
  }, [dismissMobileSidebar, primaryAction, usagePercent]);
  const { trackSideNavCreditUsagePopoverExtendTrialClick } = ports;
  const handleExtendTrialClick = useCallback(() => {
    const { usagePercent } = analyticsContextRef.current;
    trackSideNavCreditUsagePopoverExtendTrialClick({
      usagePercent,
      targetPlan,
      label: 'Extend your free trial',
      ...(newUserTier != null && { currentPlan: newUserTier }),
    });
    openTrialExtensionModal();
  }, [
    newUserTier,
    openTrialExtensionModal,
    targetPlan,
    trackSideNavCreditUsagePopoverExtendTrialClick,
  ]);

  const { trackSideNavCreditUsagePopoverCloseClick } = ports;
  const handleDismissClick = useCallback(() => {
    const { usagePercent } = analyticsContextRef.current;
    trackSideNavCreditUsagePopoverCloseClick({
      usagePercent,
      targetPlan,
      label: intl.formatMessage({ defaultMessage: 'Close', id: 'rbrahOGMC3' }),
      ...(newUserTier != null && { currentPlan: newUserTier }),
    });
  }, [intl, newUserTier, targetPlan, trackSideNavCreditUsagePopoverCloseClick]);

  const closeTrialCard = useCallback(() => {
    if (isManualOpen) setIsManualOpen(false);
    if (isQueuedOpen) handleQueuedModalClose();
  }, [handleQueuedModalClose, isManualOpen, isQueuedOpen]);
  const closeTrialCardThen = useCallback(
    (next: () => void) => {
      afterCardCloseRef.current = next;
      closeTrialCard();
    },
    [closeTrialCard]
  );
  // Same routing and tracking as the tile's own upgrade: pricing plans under the
  // full upgrade flow, the Clinician upgrade modal otherwise.
  const handleTrialCardUpgrade = useCallback(() => {
    if (upgradeAction != null) closeTrialCardThen(upgradeAction);
  }, [closeTrialCardThen, upgradeAction]);
  const handleTrialCardExtend = useCallback(() => {
    const { usagePercent } = analyticsContextRef.current;
    trackSideNavCreditUsagePopoverExtendTrialClick({
      usagePercent,
      targetPlan,
      label: 'Extend your trial',
      ...(newUserTier != null && { currentPlan: newUserTier }),
    });
    closeTrialCardThen(openTrialExtensionModal);
  }, [
    closeTrialCardThen,
    newUserTier,
    openTrialExtensionModal,
    targetPlan,
    trackSideNavCreditUsagePopoverExtendTrialClick,
  ]);
  const handleTrialCardDismiss = useCallback(() => {
    handleDismissClick();
    closeTrialCard();
  }, [closeTrialCard, handleDismissClick]);
  // Fires after the exit animation (at once under reduced motion). Keeping focus
  // off the trigger lets the next dialog take it.
  const handleTrialCardCloseAutoFocus = useCallback((event: Event) => {
    const next = afterCardCloseRef.current;
    if (next == null) return;
    afterCardCloseRef.current = null;
    event.preventDefault();
    next();
  }, []);

  const hasUpgradeAction = primaryAction != null;
  const isTrialEndCountdown = lastWeekCountdown != null;
  const sidebarTooltip = useMemo(
    () =>
      showsCardOnHover
        ? undefined
        : trialCountdownLabel == null
          ? tooltipLabel
          : isTrialEndCountdown
            ? {
                children: (
                  <span data-testid="side-nav-trial-end-tooltip">
                    {trialCountdownLabel}
                  </span>
                ),
              }
            : {
                className: 'px-3 py-2 text-center',
                children: (
                  <>
                    <TypographyP2SemiBold>{trialCountdownLabel}</TypographyP2SemiBold>
                    {hasUpgradeAction ? (
                      <TypographyCaption className="block text-text-secondary-on-dark">
                        {intl.formatMessage(sideNavCreditUsagePopoverMessages.upgrade)}
                      </TypographyCaption>
                    ) : null}
                  </>
                ),
              },
    [
      hasUpgradeAction,
      intl,
      isTrialEndCountdown,
      showsCardOnHover,
      tooltipLabel,
      trialCountdownLabel,
    ]
  );
  const triggerAriaLabel = nudgeCard?.title ?? trialCountdownLabel ?? tooltipLabel;
  const iconPlanOverride = nudge.trigger.iconPlan;
  const triggerHoverHandlers = showsCardOnHover ? hover.triggerHandlers : undefined;
  const learnMoreHref = ports.getUsageLimitsHelpUrl(intl.locale);
  // The trial card opens on click, so a hover tooltip would only stack on it.
  const triggerTooltip = trialCard != null ? undefined : sidebarTooltip;

  const renderTrigger = useMemo(
    () =>
      useSidebarTrigger
        ? ({
            plan: triggerPlan,
            triggerButtonClass,
            triggerVariant: resolvedTriggerVariant,
            countdownValue,
            'data-testid': buttonTestId,
          }: {
            plan: typeof plan;
            triggerButtonClass?: string;
            triggerVariant: typeof triggerVariant;
            countdownValue?: number;
            'data-testid': string;
          }) => (
            <SidebarMenuButton
              layout="iconOnly"
              tooltip={triggerTooltip}
              aria-label={triggerAriaLabel}
              className={triggerButtonClass}
              data-testid={buttonTestId}
              onPointerEnter={triggerHoverHandlers?.onPointerEnter}
              onPointerLeave={triggerHoverHandlers?.onPointerLeave}
              onClick={triggerHoverHandlers ? handleTriggerClick : undefined}
            >
              {countdownValue != null ? (
                <TypographyP2SemiBold className="leading-none text-text-primary-on-dark">
                  {countdownValue}
                </TypographyP2SemiBold>
              ) : (
                <CreditUsageTriggerIcon
                  plan={iconPlanOverride ?? triggerPlan}
                  variant={resolvedTriggerVariant}
                  size={16}
                />
              )}
            </SidebarMenuButton>
          )
        : undefined,
    [
      handleTriggerClick,
      iconPlanOverride,
      triggerAriaLabel,
      triggerHoverHandlers,
      triggerTooltip,
      useSidebarTrigger,
    ]
  );

  const renderPanel = useMemo(
    () =>
      nudgeCard == null
        ? undefined
        : ({
            closeAfterAction,
            dismiss,
            ctaButtonRef,
          }: {
            closeAfterAction: () => void;
            dismiss: () => void;
            ctaButtonRef: Ref<HTMLButtonElement>;
          }) => (
            <UsageNudgeCard
              title={nudgeCard.title}
              body={nudgeCard.body}
              learnMoreHref={learnMoreHref}
              {...(nudgeCta != null && {
                cta: {
                  label: nudgeCta.label,
                  icon: nudgeCta.icon,
                  onClick: () => {
                    handleNudgeCtaClick();
                    closeAfterAction();
                  },
                },
              })}
              onCloseClick={dismiss}
              ctaButtonRef={ctaButtonRef}
              data-testid={`${testId}-nudge-card`}
            />
          ),
    [handleNudgeCtaClick, learnMoreHref, nudgeCard, nudgeCta, testId]
  );

  const popover = (
    <CreditUsageIndicatorPopover
      plan={plan}
      hasUser={!!user}
      isOnClinicianMax={isOnMaxSeat}
      isHeidi2OptInEnabled={isOptInCreditUsageEnabled}
      isMaxUpgradeEnabled={isMaxUpgradeEnabled}
      trialRemaining={trialRemaining}
      {...(lastWeekCountdown != null && { countdownDays: lastWeekCountdown.daysLeft })}
      upgradeSeat={upgradeSeat}
      upgradeCtaLabel={upgradeCtaLabel}
      triggerVariant={triggerVariant}
      open={isOpen}
      onUpgradeClick={upgradeAction}
      onExtendTrialClick={
        ports.isAppleCompliantBuild() || isOfflineGated || !isTrialExtensionEntryEnabled
          ? undefined
          : handleExtendTrialClick
      }
      onOpenChange={handleOpenChange}
      onDismissClick={handleDismissClick}
      learnMoreHref={learnMoreHref}
      side={side}
      align={align}
      sideOffset={sideOffset}
      renderTrigger={renderTrigger}
      renderPanel={renderPanel}
      focusCtaOnOpen={!isHoverOpen}
      {...(showsCardOnHover && {
        onContentPointerEnter: hover.contentHandlers.onPointerEnter,
        onContentPointerLeave: hover.contentHandlers.onPointerLeave,
      })}
      {...(trialCard != null && {
        renderContent: ({ upgradeButtonRef }) => (
          <ClinicianTrialNavCard
            phase={trialCard.phase}
            daysLeft={trialCard.daysLeft}
            trialEnd={trialCard.trialEnd}
            learnMoreHref={learnMoreHref}
            onUpgrade={handleTrialCardUpgrade}
            {...(isTrialExtensionAvailable &&
              !isOfflineGated && {
                onExtendTrial: handleTrialCardExtend,
              })}
            onDismiss={handleTrialCardDismiss}
            upgradeButtonRef={upgradeButtonRef}
          />
        ),
        onCloseAutoFocus: handleTrialCardCloseAutoFocus,
      })}
      data-testid={testId}
    />
  );

  // Mobile has no popover: the row is the upgrade CTA. No row when there is
  // no upgrade path (Apple builds, Max on a capped cycle, Max upgrade not enabled).
  if (isMobile) {
    if (primaryAction == null) return null;

    const upgradeLabel = intl.formatMessage(sideNavCreditUsagePopoverMessages.upgrade);
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            aria-label={upgradeLabel}
            onClick={handleMobileClick}
            className={
              triggerVariant === 'tile' ? CLINICIAN_MAX_UPGRADE_ROW_INK_CLASS : undefined
            }
            data-testid={testId}
          >
            <CreditUsageTriggerIcon
              plan={plan}
              variant={triggerVariant}
              className={
                triggerVariant === 'tile'
                  ? CLINICIAN_MAX_UPGRADE_ROW_INK_CLASS
                  : undefined
              }
            />
            <span>{upgradeLabel}</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  if (!useSidebarTrigger) return popover;

  return (
    <SidebarMenu className="items-center gap-2">
      <SidebarMenuItem>{popover}</SidebarMenuItem>
    </SidebarMenu>
  );
}
